import { useEffect, useMemo, useState } from 'react'
import AdminShell from './AdminShell'
import {
  AdminButton,
  AdminCard,
  AdminCardHeader,
  AdminField,
  AdminInput,
  AdminModal,
  AdminSkeleton,
  AdminStatePanel,
  AdminStatusPill,
} from './AdminUi'
import '../styles/admin-system-settings.css'

type SessionPayload = {
  authenticated: true
  user: { email: string; role: string }
  csrfToken: string
}

type AnnouncementSettings = {
  schemaVersion: 1
  version: number
  enabled: boolean
  message: string
  promoCode: string | null
  ctaLabel: string | null
  ctaUrl: string | null
  startsAt: string | null
  endsAt: string | null
  updatedAt: string | null
  updatedBy: string | null
}

type SettingsPayload = {
  bankTransfer: {
    accountName: string
    accountNumber: string
    version: number
    updatedAt: string
  }
  siteAnnouncement: AnnouncementSettings
  fixed: {
    businessName: string
    operatingContext: string
    currency: string
    timeZone: string
    customerAccounts: string
    receiptsInvoices: string
    paymentMethods: string[]
  }
  ownership: Record<string, string>
}

type ApiEnvelope = { settings?: SettingsPayload; saved?: boolean; unchanged?: boolean; code?: string; message?: string }

type SaveState = 'unchanged' | 'unsaved' | 'saving' | 'saved' | 'stale' | 'error'

const ACCOUNT_PATTERN = /^\d{2}-\d{4}-\d{7}-\d{3}$/

function environmentFromHostname(hostname: string) {
  const host = hostname.toLowerCase()
  if (host.includes('staging')) return 'Staging'
  if (host === 'localhost' || host === '127.0.0.1') return 'Local'
  return undefined
}

function maskAccount(value: string) {
  return ACCOUNT_PATTERN.test(value) ? `${value.slice(0, 2)}-••••-•••••••-${value.slice(-3)}` : '••-••••-•••••••-•••'
}

function localDateTimeValue(iso: string | null) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 16)
}

function isoFromLocalDateTime(value: string) {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}


function statusTone(state: SaveState): 'neutral' | 'warning' | 'success' | 'danger' {
  if (state === 'saved') return 'success'
  if (state === 'unsaved' || state === 'saving' || state === 'stale') return 'warning'
  if (state === 'error') return 'danger'
  return 'neutral'
}

function statusLabel(state: SaveState) {
  if (state === 'unsaved') return 'Unsaved changes'
  if (state === 'saving') return 'Saving…'
  if (state === 'saved') return 'Saved'
  if (state === 'stale') return 'Refresh required'
  if (state === 'error') return 'Needs attention'
  return 'Up to date'
}

export default function AdminSystemSettingsPage() {
  const [session, setSession] = useState<SessionPayload | null>(null)
  const [settings, setSettings] = useState<SettingsPayload | null>(null)
  const [accountName, setAccountName] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [state, setState] = useState<SaveState>('unchanged')
  const [loading, setLoading] = useState(true)
  const [logoutBusy, setLogoutBusy] = useState(false)
  const [error, setError] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [announcementState, setAnnouncementState] = useState<SaveState>('unchanged')
  const [announcementEnabled, setAnnouncementEnabled] = useState(false)
  const [announcementMessage, setAnnouncementMessage] = useState('')
  const [announcementPromoCode, setAnnouncementPromoCode] = useState('')
  const [announcementCtaLabel, setAnnouncementCtaLabel] = useState('')
  const [announcementCtaUrl, setAnnouncementCtaUrl] = useState('')
  const [announcementStartsAt, setAnnouncementStartsAt] = useState('')
  const [announcementEndsAt, setAnnouncementEndsAt] = useState('')
  const environmentLabel = useMemo(() => environmentFromHostname(window.location.hostname), [])

  const dirty = settings !== null
    && (accountName !== settings.bankTransfer.accountName || accountNumber !== settings.bankTransfer.accountNumber)

  const announcementDirty = settings !== null && (
    announcementEnabled !== settings.siteAnnouncement.enabled
    || announcementMessage !== settings.siteAnnouncement.message
    || announcementPromoCode !== (settings.siteAnnouncement.promoCode ?? '')
    || announcementCtaLabel !== (settings.siteAnnouncement.ctaLabel ?? '')
    || announcementCtaUrl !== (settings.siteAnnouncement.ctaUrl ?? '')
    || announcementStartsAt !== localDateTimeValue(settings.siteAnnouncement.startsAt)
    || announcementEndsAt !== localDateTimeValue(settings.siteAnnouncement.endsAt)
  )

  useEffect(() => {
    if (state === 'saving' || state === 'stale' || state === 'error') return
    setState(dirty ? 'unsaved' : state === 'saved' ? 'saved' : 'unchanged')
  }, [dirty, state])

  useEffect(() => {
    if (announcementState === 'saving' || announcementState === 'stale' || announcementState === 'error') return
    setAnnouncementState(announcementDirty ? 'unsaved' : announcementState === 'saved' ? 'saved' : 'unchanged')
  }, [announcementDirty, announcementState])

  async function load() {
    setLoading(true)
    setError('')
    try {
      const [sessionResponse, settingsResponse] = await Promise.all([
        fetch('/api/admin/session', { credentials: 'same-origin', cache: 'no-store' }),
        fetch('/api/admin/settings', { credentials: 'same-origin', cache: 'no-store' }),
      ])
      if (sessionResponse.status === 401 || settingsResponse.status === 401) {
        window.location.replace('/admin/login?next=/admin/settings')
        return
      }
      if (!sessionResponse.ok) throw new Error('Boss Zone session could not be loaded.')
      const sessionPayload = await sessionResponse.json() as SessionPayload
      const envelope = await settingsResponse.json().catch(() => null) as ApiEnvelope | null
      if (!settingsResponse.ok || !envelope?.settings) throw new Error(envelope?.message || 'System Settings could not be loaded.')
      setSession(sessionPayload)
      setSettings(envelope.settings)
      setAccountName(envelope.settings.bankTransfer.accountName)
      setAccountNumber(envelope.settings.bankTransfer.accountNumber)
      setAnnouncementEnabled(envelope.settings.siteAnnouncement.enabled)
      setAnnouncementMessage(envelope.settings.siteAnnouncement.message)
      setAnnouncementPromoCode(envelope.settings.siteAnnouncement.promoCode ?? '')
      setAnnouncementCtaLabel(envelope.settings.siteAnnouncement.ctaLabel ?? '')
      setAnnouncementCtaUrl(envelope.settings.siteAnnouncement.ctaUrl ?? '')
      setAnnouncementStartsAt(localDateTimeValue(envelope.settings.siteAnnouncement.startsAt))
      setAnnouncementEndsAt(localDateTimeValue(envelope.settings.siteAnnouncement.endsAt))
      setState('unchanged')
      setAnnouncementState('unchanged')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'System Settings could not be loaded.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  function localValidation() {
    const normalizedName = accountName.replace(/\s+/g, ' ').trim()
    if (normalizedName.length < 2 || normalizedName.length > 80 || /<|>|https?:\/\/|www\.|javascript:|data:/i.test(normalizedName)) {
      return 'Enter a plain-text account name between 2 and 80 characters.'
    }
    if (!ACCOUNT_PATTERN.test(accountNumber.trim())) return 'Enter the NZ account number as ##-####-#######-###.'
    return ''
  }

  function requestSave() {
    if (!settings || !dirty || state === 'saving') return
    const validation = localValidation()
    if (validation) {
      setError(validation)
      setState('error')
      return
    }
    setError('')
    setConfirmOpen(true)
  }

  async function confirmSave() {
    if (!session || !settings || state === 'saving') return
    setState('saving')
    setError('')
    try {
      const response = await fetch('/api/admin/settings/bank-transfer', {
        method: 'PUT',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': session.csrfToken,
        },
        body: JSON.stringify({
          accountName,
          accountNumber,
          expectedVersion: settings.bankTransfer.version,
        }),
      })
      const envelope = await response.json().catch(() => null) as ApiEnvelope | null
      if (response.status === 409) {
        setState('stale')
        setError(envelope?.message || 'System settings changed in another session. Refresh and review the latest values.')
        return
      }
      if (!response.ok || !envelope?.settings) {
        setState('error')
        setError(envelope?.message || 'Bank transfer settings could not be saved.')
        return
      }
      setSettings(envelope.settings)
      setAccountName(envelope.settings.bankTransfer.accountName)
      setAccountNumber(envelope.settings.bankTransfer.accountNumber)
      setState(envelope.saved ? 'saved' : 'unchanged')
      setConfirmOpen(false)
    } catch {
      setState('error')
      setError('Bank transfer settings could not be saved.')
    }
  }

  function announcementValidation() {
    const message = announcementMessage.replace(/\s+/g, ' ').trim()
    const promoCode = announcementPromoCode.replace(/\s+/g, ' ').trim()
    const ctaLabel = announcementCtaLabel.replace(/\s+/g, ' ').trim()
    const ctaUrl = announcementCtaUrl.trim()

    if (!message || message.length > 120) return 'Announcement message must be 1–120 characters.'
    if (promoCode.length > 20) return 'Promo code must be 20 characters or fewer.'
    if (ctaLabel.length > 24) return 'CTA label must be 24 characters or fewer.'
    if (Boolean(ctaLabel) !== Boolean(ctaUrl)) return 'CTA label and CTA URL must either both be filled in or both be blank.'
    if (ctaUrl) {
      const internal = ctaUrl.startsWith('/') && !ctaUrl.startsWith('//')
      let secureExternal = false
      try { secureExternal = new URL(ctaUrl).protocol === 'https:' } catch { secureExternal = false }
      if (!internal && !secureExternal) return 'CTA URL must be an internal path or an HTTPS URL.'
    }

    const startsAt = isoFromLocalDateTime(announcementStartsAt)
    const endsAt = isoFromLocalDateTime(announcementEndsAt)
    if (announcementStartsAt && !startsAt) return 'Start time is invalid.'
    if (announcementEndsAt && !endsAt) return 'End time is invalid.'
    if (startsAt && endsAt && Date.parse(endsAt) <= Date.parse(startsAt)) return 'End time must be after start time.'
    return ''
  }

  async function saveAnnouncement() {
    if (!session || !settings || !announcementDirty || announcementState === 'saving' || announcementState === 'stale') return
    const validation = announcementValidation()
    if (validation) {
      setError(validation)
      setAnnouncementState('error')
      return
    }

    setAnnouncementState('saving')
    setError('')
    try {
      const response = await fetch('/api/admin/settings/site-announcement', {
        method: 'PUT',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': session.csrfToken,
        },
        body: JSON.stringify({
          enabled: announcementEnabled,
          message: announcementMessage,
          promoCode: announcementPromoCode || null,
          ctaLabel: announcementCtaLabel || null,
          ctaUrl: announcementCtaUrl || null,
          startsAt: isoFromLocalDateTime(announcementStartsAt),
          endsAt: isoFromLocalDateTime(announcementEndsAt),
          expectedVersion: settings.siteAnnouncement.version,
        }),
      })
      const envelope = await response.json().catch(() => null) as ApiEnvelope | null
      if (response.status === 409) {
        setAnnouncementState('stale')
        setError(envelope?.message || 'Site announcement changed in another session. Refresh and review the latest values.')
        return
      }
      if (!response.ok || !envelope?.settings) {
        setAnnouncementState('error')
        setError(envelope?.message || 'Site announcement settings could not be saved.')
        return
      }

      setSettings(envelope.settings)
      setAnnouncementEnabled(envelope.settings.siteAnnouncement.enabled)
      setAnnouncementMessage(envelope.settings.siteAnnouncement.message)
      setAnnouncementPromoCode(envelope.settings.siteAnnouncement.promoCode ?? '')
      setAnnouncementCtaLabel(envelope.settings.siteAnnouncement.ctaLabel ?? '')
      setAnnouncementCtaUrl(envelope.settings.siteAnnouncement.ctaUrl ?? '')
      setAnnouncementStartsAt(localDateTimeValue(envelope.settings.siteAnnouncement.startsAt))
      setAnnouncementEndsAt(localDateTimeValue(envelope.settings.siteAnnouncement.endsAt))
      setAnnouncementState('saved')
    } catch {
      setAnnouncementState('error')
      setError('Site announcement settings could not be saved.')
    }
  }

  async function logout() {
    if (!session || logoutBusy) return
    setLogoutBusy(true)
    try {
      const response = await fetch('/api/admin/logout', {
        method: 'POST', credentials: 'same-origin', headers: { 'X-CSRF-Token': session.csrfToken },
      })
      if (!response.ok) throw new Error('logout failed')
      window.location.replace('/admin/login')
    } catch {
      setError('Logout could not be completed. Please try again.')
      setLogoutBusy(false)
    }
  }

  if (!loading && error && (!session || !settings)) {
    return <main className="admin-shell-gate-error"><AdminStatePanel state="error" title="System Settings unavailable" description={error} action={<AdminButton type="button" onClick={() => void load()}>Retry</AdminButton>} /></main>
  }
  if (!session || !settings) {
    return <main className="admin-shell-gate-loading" aria-label="Loading System Settings"><AdminCard><AdminSkeleton lines={8} /></AdminCard></main>
  }

  return (
    <AdminShell
      title="System Settings"
      eyebrow="SYSTEM"
      description="Owner-safe general business configuration and fixed system context."
      user={session.user}
      onLogout={() => void logout()}
      logoutBusy={logoutBusy}
      environmentLabel={environmentLabel}
    >
      <div className="admin-system-settings__status-row">
        <AdminStatusPill tone={statusTone(state)}>{statusLabel(state)}</AdminStatusPill>
        {state === 'stale' && <AdminButton variant="secondary" compact type="button" onClick={() => void load()}>Refresh latest</AdminButton>}
      </div>
      {error && <div className="admin-system-settings__error" role="alert">{error}</div>}

      <div className="admin-system-settings__grid">
        <AdminCard className="admin-system-settings__bank" padding="large">
          <AdminCardHeader
            title="Bank transfer details"
            description="High-impact payment destination used for new bank-transfer orders. Existing orders keep the details issued when they were created."
            action={<AdminStatusPill tone="warning">Sensitive business setting</AdminStatusPill>}
          />
          <div className="admin-system-settings__warning">
            Changing these details affects <strong>new bank-transfer orders only</strong>. Review the old and new destination carefully before saving.
          </div>
          <div className="admin-system-settings__fields">
            <AdminField label="Account name" hint="Plain text only. 2–80 characters." required>
              <AdminInput value={accountName} maxLength={80} autoComplete="off" onChange={(event) => { setAccountName(event.target.value); setError('') }} />
            </AdminField>
            <AdminField label="NZ account number" hint="Format: ##-####-#######-###" required>
              <AdminInput value={accountNumber} inputMode="numeric" maxLength={19} autoComplete="off" onChange={(event) => { setAccountNumber(event.target.value); setError('') }} />
            </AdminField>
          </div>
          <div className="admin-system-settings__save-row">
            <span>Version {settings.bankTransfer.version}</span>
            <AdminButton type="button" disabled={!dirty || state === 'saving' || state === 'stale'} onClick={requestSave}>
              {state === 'saving' ? 'Saving…' : 'Review & save'}
            </AdminButton>
          </div>
        </AdminCard>

        <AdminCard padding="large">
          <AdminCardHeader title="Business & system context" description="Fixed or separately-owned values are informational here." />
          <dl className="admin-system-settings__facts">
            <div><dt>Business</dt><dd>{settings.fixed.businessName}<small>{settings.fixed.operatingContext}</small></dd></div>
            <div><dt>Currency</dt><dd>NZD<small>Fixed</small></dd></div>
            <div><dt>Operational timezone</dt><dd>{settings.fixed.timeZone}<small>Fixed</small></dd></div>
            <div><dt>Customer accounts</dt><dd>Disabled<small>Not implemented</small></dd></div>
            <div><dt>Receipts / invoices</dt><dd>Deferred<small>Not enabled</small></dd></div>
            <div><dt>Payment methods</dt><dd>{settings.fixed.paymentMethods.join(' / ')}<small>Informational</small></dd></div>
          </dl>
        </AdminCard>
      </div>

      <AdminCard className="admin-system-settings__announcement" padding="large">
        <AdminCardHeader
          title="Site announcement"
          description="Shared customer-facing announcement shown above the public navigation. Dismissibility is fixed off."
          action={<AdminStatusPill tone={announcementEnabled ? 'pink' : 'neutral'}>{announcementEnabled ? 'Enabled' : 'Disabled'}</AdminStatusPill>}
        />
        <div className="admin-system-settings__announcement-grid">
          <label className="admin-system-settings__toggle">
            <input
              type="checkbox"
              checked={announcementEnabled}
              onChange={(event) => { setAnnouncementEnabled(event.target.checked); setError('') }}
            />
            <span><strong>Enable announcement</strong><small>Applies to public customer pages only.</small></span>
          </label>

          <AdminField label="Message" hint="Required. Maximum 120 characters." required>
            <textarea
              className="admin-ui-input admin-system-settings__textarea"
              value={announcementMessage}
              maxLength={120}
              rows={3}
              onChange={(event) => { setAnnouncementMessage(event.target.value); setError('') }}
            />
          </AdminField>

          <div className="admin-system-settings__announcement-fields">
            <AdminField label="Promo code" hint="Optional. Maximum 20 characters.">
              <AdminInput value={announcementPromoCode} maxLength={20} autoComplete="off" onChange={(event) => { setAnnouncementPromoCode(event.target.value); setError('') }} />
            </AdminField>
            <AdminField label="CTA label" hint="Optional. Maximum 24 characters.">
              <AdminInput value={announcementCtaLabel} maxLength={24} autoComplete="off" onChange={(event) => { setAnnouncementCtaLabel(event.target.value); setError('') }} />
            </AdminField>
            <AdminField label="CTA URL" hint="Optional. Internal path or HTTPS URL.">
              <AdminInput value={announcementCtaUrl} maxLength={500} autoComplete="off" placeholder="/order" onChange={(event) => { setAnnouncementCtaUrl(event.target.value); setError('') }} />
            </AdminField>
          </div>

          <div className="admin-system-settings__announcement-fields admin-system-settings__announcement-fields--schedule">
            <AdminField label="Starts at" hint="Optional. Uses your device local time and is saved as an unambiguous UTC timestamp.">
              <AdminInput type="datetime-local" value={announcementStartsAt} onChange={(event) => { setAnnouncementStartsAt(event.target.value); setError('') }} />
            </AdminField>
            <AdminField label="Ends at" hint="Optional. The announcement stops at this time.">
              <AdminInput type="datetime-local" value={announcementEndsAt} onChange={(event) => { setAnnouncementEndsAt(event.target.value); setError('') }} />
            </AdminField>
          </div>
        </div>

        <div className="admin-system-settings__save-row">
          <span>
            Version {settings.siteAnnouncement.version}
            {settings.siteAnnouncement.updatedAt ? ` · Saved ${new Date(settings.siteAnnouncement.updatedAt).toLocaleString('en-NZ')}` : ' · Default configuration'}
          </span>
          <div className="admin-system-settings__save-actions">
            <AdminStatusPill tone={statusTone(announcementState)}>{statusLabel(announcementState)}</AdminStatusPill>
            {announcementState === 'stale'
              ? <AdminButton variant="secondary" compact type="button" onClick={() => void load()}>Refresh latest</AdminButton>
              : null}
            <AdminButton
              type="button"
              disabled={!announcementDirty || announcementState === 'saving' || announcementState === 'stale'}
              onClick={() => void saveAnnouncement()}
            >
              {announcementState === 'saving' ? 'Saving…' : 'Save announcement'}
            </AdminButton>
          </div>
        </div>
      </AdminCard>

      <AdminCard className="admin-system-settings__ownership">
        <AdminCardHeader title="Managed elsewhere" description="These controls stay with their owning Boss Zone pages rather than being duplicated here." />
        <div className="admin-system-settings__links">
          <a href={settings.ownership.storeStatus}>Store Status<span>Orders, schedule, capacity</span></a>
          <a href={settings.ownership.pricingDelivery}>Pricing & Delivery<span>Pack prices, delivery fees</span></a>
          <a href={settings.ownership.weeklyMenu}>Weekly Menu<span>Flavours, sold out, publication</span></a>
          <a href={settings.ownership.contentMedia}>Content & Media<span>Homepage copy and product media</span></a>
          <a href={settings.ownership.emailNotifications}>Email & Notifications<span>Email monitoring and notification settings</span></a>
        </div>
      </AdminCard>

      <AdminModal
        open={confirmOpen}
        title="Confirm bank transfer change"
        description="This changes where new bank-transfer customers are instructed to pay. Existing order snapshots will not change."
        onClose={() => state !== 'saving' && setConfirmOpen(false)}
        footer={<>
          <AdminButton variant="secondary" type="button" disabled={state === 'saving'} onClick={() => setConfirmOpen(false)}>Cancel</AdminButton>
          <AdminButton type="button" disabled={state === 'saving'} onClick={() => void confirmSave()}>{state === 'saving' ? 'Saving…' : 'Confirm change'}</AdminButton>
        </>}
      >
        <div className="admin-system-settings__review">
          <div><span>Account name</span><del>{settings.bankTransfer.accountName}</del><strong>{accountName.replace(/\s+/g, ' ').trim()}</strong></div>
          <div><span>Account number</span><del>{maskAccount(settings.bankTransfer.accountNumber)}</del><strong>{maskAccount(accountNumber.trim())}</strong></div>
        </div>
      </AdminModal>
    </AdminShell>
  )
}
